import { Global } from "../classes/Global";
import { InternationalizationHelper } from "./InternationalizationHelper";

interface ServerDate {
    offset: number;
    timezone: string;
    value: string;
}

export class DateHelper {

    static serverDateObjToDateString(serverDate: ServerDate): string {
        if(!serverDate) { return ""; }
        const clientOffset = -(new Date().getTimezoneOffset() * 60);
        const serverOffset = serverDate.offset;
        const offsetDiff = clientOffset - serverOffset;

        let date: Date = new Date(serverDate.value);
        date.setSeconds(date.getSeconds() + offsetDiff);

        return this.getLocaleDateString(date);

    }

  static getDayString(date: string): string {

    let d: Date = DateHelper.dateStringToDateObject(date);
    if(d == null) {
        return "";
    }

    let today: Date = new Date();
    let yesterday: Date = new Date();
    yesterday.setDate(yesterday.getDate() -1);

    if(d.toDateString() == today.toDateString()) {
        return "today";
    }
    else if(d.toDateString() == yesterday.toDateString()) {
        return "yesterday";
    }
    else {
        return "";
    }

  }

    /**
     * Take a date as string and make a Data Object out of it.
     * Returns null if the string input is not in a valid format
     * The format must be YYYY-MM-DD
     */
    static dateStringToDateObject(date:string): Date {

        if(date){
            let d = new String(date);
            let dateParts = d.split('-');
            if(dateParts.length != 3){
                return null;
            }
            let timeParts = d.split(' ');
            let hours = 0, minutes = 0, seconds = 0;
            if(timeParts.length == 2) {
              let time = timeParts[1].split(':');
              hours = Number.parseInt(time[0]);
              minutes = Number.parseInt(time[1]);
              seconds = Number.parseInt(time[2]);
            }

            let year = Number.parseInt(dateParts[0]);
            let month = Number.parseInt(dateParts[1]);
            let day = Number.parseInt(dateParts[2]);
            return new Date(year, month-1, day, hours, minutes, seconds); //months are 0-based
        }
        else {
            return null;
        }

    }

  static getLocaleDateString(date: Date, shortYear: boolean = false): string {
    if (date && date instanceof Date) {
      date.setHours(12); // Set to the middle of the day to avoid timezone problems

      const locale = InternationalizationHelper.getNavigatorLocale(Global.getCountry());
      const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: shortYear ? '2-digit' : 'numeric'
      };

      return date.toLocaleDateString(locale, options);
    } else {
      return "";
    }
  }


    /**
     * Returns the date and time formatted according to the locale. DOes not display the seconds
     * @param date
     */
    static getLocaleDateTimeString(date: Date): string {

      if(date && date instanceof Date){
          return date.toLocaleDateString(InternationalizationHelper.getTwoCharactersLanguage()) + ' ' +
          date.toLocaleTimeString(InternationalizationHelper.getTwoCharactersLanguage(), {hour: '2-digit', minute:'2-digit'});
      }
      else {
          return "";
      }

  }

  /**
     * Takes a date in any valid format, converts it to the right format
     * according to the user's locale and return the resulting date as a string
     */
  static serverDateToLocaleDate(date: string, shortYear: boolean = false): string {

        let d = DateHelper.dateStringToDateObject(date);

        if(d){
          return DateHelper.getLocaleDateString(d, shortYear);
        }
        else if(date != null){
            //return date as it is
            return date;
        }
        else {
            return "";
        }

    }

    /**
     * Takes a date in any valid format, converts it to the right format
     * according to the user's locale and return the resulting date as a string
     */
    static serverDateToLocaleDateTime(date:string): string {

      let d = DateHelper.dateStringToDateObject(date);

      if(d){
          return DateHelper.getLocaleDateTimeString(d);
      }
      else if(date != null){
          //return date as it is
          return date;
      }
      else {
          return "";
      }

  }

    /**
     *
     * @param date date object
     * @param precision "year", "month", "day", "hour", "minute". Up to seconds by default
     */
    static getTimeAgo(date: Date, precision:string) {

        let seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

        let interval = Math.floor(seconds / 31536000);

        if(interval == 1) {
          return {value: interval, unit: "year"};
        }
        if (interval > 1) {
          return {value: interval, unit: "years"};
        }
        if (precision == "year") {
            return {value: interval, unit: "year", text: "thisYear"};
        }

        interval = Math.floor(seconds / 2592000);
        if(interval == 1) {
          return {value: interval, unit: "month"};
        }
        if (interval > 1) {
          return {value: interval, unit: "months"};
        }
        if (precision == "month") {
            return {value: interval, unit: "month", text: "thisMonth"};
        }

        interval = Math.floor(seconds / 86400);
        if(interval == 1) {
            return {value: interval, unit: "day"};
        }
        if (interval > 1) {
            return {value: interval, unit: "days"};
        }
        if(precision == "day") {
            return {value: interval, unit: "day", text: "today"};
        }

        interval = Math.floor(seconds / 3600);
        if(interval == 1) {
          return {value: interval, unit: "hour"};
        }
        if (interval > 1) {
          return {value: interval, unit: "hours"};
        }
        if(precision == "hour") {
            return {value: interval, unit: "hour", text:"inTheLastHour"};
        }

        interval = Math.floor(seconds / 60);
        if(interval == 1) {
          return {value: interval, unit: "minute"};
        }
        if (interval > 1) {
          return {value: interval, unit: "minutes"};
        }
        if (precision == "minute") {
            return {value: interval, unit: "minute", text: "inTheLastMinute"};
        }

        interval = Math.floor(seconds);
        if(interval == 1) {
          return {value: interval, unit: "second"};
        }
        return {value: interval, unit: "seconds"};

      }

    static isBlackFriday(): boolean {
        //code simplification (for readability) and adaptation of the solution found here:
        //https://codegolf.stackexchange.com/a/64797
        const now = new Date();
        const blackFridayDate: number = 29 - new Date(now.getFullYear(),8).getDay();
        const previousMonday: Date = new Date(now.getFullYear(), 10, blackFridayDate - 4);
        const followingSunday: Date = new Date(now.getFullYear(), 10, blackFridayDate + 2);
        return now >= previousMonday && now <= followingSunday;
    }

}
